import React from "react"

import { Link, type Location, useLocation } from "react-router-dom"

import { isNullOrUndefined } from "$/utils/gates"

import {
    Button,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TProductTypeQuery, useProductTypeQuery } from "@/3514/queries"
import { CARE_TEAM_EMAIL, INDEX_URL, ParticipantUrls, WISER_COACHING_LINK } from "@/constants"
import { SURVEY_STATUS } from "@/entities/survey/constants"
import { SurveyStatus } from "@/entities/survey/models"
import { type IUseTranslation, useTranslation } from "@/hooks"
import CareTeamLink from "@/shared/CareTeamLink"
import { LearnMore } from "@/shared/learn-more/LearnMore"

type Props = {
    status: SurveyStatus
    isSelfAssessment: boolean
}

export const FeedbackSurvey = ({ status, isSelfAssessment }: Props) => {
    const { data: productType }: TProductTypeQuery = useProductTypeQuery()

    const location: Location = useLocation()

    const { t }: IUseTranslation = useTranslation()

    const productTypeData = isNullOrUndefined(productType)
        ? {
              supportEmail: CARE_TEAM_EMAIL.split(":")[1],
              supportTeamName: "Wiser Care"
          }
        : productType

    const text = {
        [SURVEY_STATUS.COMPLETED]: {
            heading: "Thank you for submitting.",
            description: "Your participation in this survey has supported the growth of another."
        },
        [SURVEY_STATUS.EXPIRED]: {
            heading: "Submission window has closed.",
            description: `This survey link has expired. If you think you’re seeing this message in error, please <a href="mailto:${productTypeData?.supportEmail}" class="stakeholder-survey-expired-care-link">contact Wiser Care.</a>`
        }
    }

    const shouldRedirectToSurveysPage: boolean = Boolean(location.state?.shouldBackButtonRedirectToSurveysPage)

    return (
        <div className="d-flex flex-column align-items-center">
            <Typography
                text={text?.[status]?.heading ?? "Thank you for submitting."}
                tag={ETypographyTag.Heading1}
                color={ETypographyColor.Black}
                alignment={ETypographyAlignment.Center}
                weight={ETypographyWeight.Medium}
                size={ETypographySize.Heading1}
                family={ETypographyFamily.DmSerif}
            />
            {!isSelfAssessment ? (
                <p
                    className="color-gray fs-14 text-center mb-50"
                    dangerouslySetInnerHTML={{ __html: text?.[status]?.description ?? "" }}
                />
            ) : (
                <div className="text-center mt-[30px]">
                    <Link to={shouldRedirectToSurveysPage ? ParticipantUrls.SURVEYS : INDEX_URL}>
                        <Button>
                            {shouldRedirectToSurveysPage
                                ? t("participantSide.surveyPage.backButton.surveys.title")
                                : "Back To Dashboard"}
                        </Button>
                    </Link>
                </div>
            )}

            {status === SURVEY_STATUS.COMPLETED && !isSelfAssessment && <LearnMore link={WISER_COACHING_LINK} />}
            <CareTeamLink
                text="Questions?"
                variant="brand"
                className="stakeholder-feedback-care-link bottom:[40px] md:!bottom-[100px]"
            />
        </div>
    )
}
